/**
 * Converts technical error messages into user-friendly Hungarian messages
 * with actionable solutions.
 */
export const getUserFriendlyError = (error: any): string => {
  const errorMessage = error?.message || String(error);
  const errorCode = error?.code || '';
  const lowerMessage = errorMessage.toLowerCase();

  // Network/Connection errors
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('failed to fetch') ||
    lowerMessage.includes('networkerror')
  ) {
    return 'Nincs internetkapcsolat. Kérlek ellenőrizd az internetkapcsolatot és próbáld újra.';
  }

  // Permission/Authentication errors
  if (
    lowerMessage.includes('permission') ||
    lowerMessage.includes('unauthorized') ||
    errorCode === 'PGRST301' ||
    lowerMessage.includes('jwt') ||
    lowerMessage.includes('token')
  ) {
    return 'Nincs jogosultságod ehhez a művelethez. Kérlek jelentkezz be újra vagy lépj kapcsolatba az adminisztrátorral.';
  }

  // Duplicate/Conflict errors
  if (
    lowerMessage.includes('duplicate') ||
    errorCode === '23505' ||
    lowerMessage.includes('already exists') ||
    lowerMessage.includes('már létezik')
  ) {
    return 'Ez az elem már létezik az adatbázisban. Használj másik azonosítót vagy szerkeszd a meglévő elemet.';
  }

  // Invalid login errors
  if (
    lowerMessage.includes('invalid login') ||
    lowerMessage.includes('invalid credentials') ||
    lowerMessage.includes('wrong password') ||
    lowerMessage.includes('user not found')
  ) {
    return 'Hibás felhasználónév vagy jelszó. Kérlek ellenőrizd a bejelentkezési adataidat.';
  }

  // Already registered errors
  if (
    lowerMessage.includes('already registered') ||
    lowerMessage.includes('user already exists') ||
    lowerMessage.includes('email already in use')
  ) {
    return 'Ez a felhasználónév vagy email már foglalt. Használj másik email címet vagy jelentkezz be.';
  }

  // Not found errors
  if (
    lowerMessage.includes('not found') ||
    lowerMessage.includes('does not exist') ||
    errorCode === 'PGRST116'
  ) {
    return 'Az elem nem található. Lehet, hogy törölve lett vagy nem létezik.';
  }

  // Validation errors
  if (
    lowerMessage.includes('validation') ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('required')
  ) {
    return 'Érvénytelen adatok. Kérlek ellenőrizd, hogy minden kötelező mező ki van-e töltve.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return 'A művelet túl sokáig tartott. Kérlek próbáld újra. Ha a probléma továbbra is fennáll, ellenőrizd az internetkapcsolatot.';
  }

  // Rate limiting errors
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many requests')) {
    return 'Túl sok kérés rövid időn belül. Kérlek várj egy pillanatot és próbáld újra.';
  }

  // Database errors
  if (
    lowerMessage.includes('database') ||
    lowerMessage.includes('connection') ||
    errorCode?.startsWith('PGRST')
  ) {
    return 'Adatbázis hiba történt. Kérlek próbáld újra később. Ha a probléma továbbra is fennáll, jelezd a támogatásnak.';
  }

  // Default fallback - return original message but in a more user-friendly format
  // Remove technical details if present
  let friendlyMessage = errorMessage;

  // Remove common technical prefixes
  friendlyMessage = friendlyMessage.replace(/^error:\s*/i, '');
  friendlyMessage = friendlyMessage.replace(/^failed:\s*/i, '');

  // If message is too technical, provide generic message
  if (friendlyMessage.includes('at ') && friendlyMessage.includes('.ts:')) {
    return 'Hiba történt a művelet során. Kérlek próbáld újra. Ha a probléma továbbra is fennáll, jelezd a támogatásnak.';
  }

  return `Hiba: ${friendlyMessage}`;
};
